import { useCallback, useEffect, useMemo, useRef } from "react"
import { Keyboard, Platform, ScrollView, TextStyle, View, ViewStyle } from "react-native"

import { useFocusEffect, useLocalSearchParams } from "expo-router"
import * as ScreenOrientation from "expo-screen-orientation"
import { StatusBar } from "expo-status-bar"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { FeedbackRating } from "types/feedback"

import type { ThemedStyle } from "src/theme"
import { Alert } from "src/components/Alert"
import { CloseButton } from "src/components/CloseButton"
import { FeedbackForm } from "src/components/FeedbackForm"
import { Text } from "src/components/Ignite"
import { SidekickView } from "src/components/SidekickView"
import { useAuth } from "src/context/AuthContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { getAppVersion } from "src/utils/appVersion"
import { backOrToRoot } from "src/utils/routerHelpers"

/**
 * FeedbackScreen - A full-screen feedback form that uses SidekickView for consistent UX.
 *
 * Features:
 * - Avatar overlay with speech integration
 * - Talk/Type/Menu button cluster
 * - Feedback form with rating and tags
 */
export default function FeedbackScreen() {
  const { api } = useAuth()
  const insets = useSafeAreaInsets()
  const { themed, navTheme } = useAppTheme()

  const scrollViewRef = useRef<ScrollView>(null)

  // Lock to portrait orientation when this screen gains focus
  useFocusEffect(
    useCallback(() => {
      ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP).catch(() => {})
    }, []),
  )

  useEffect(() => {
    const showSubscription = Keyboard.addListener(
      Platform.OS === "ios" ? "keyboardWillShow" : "keyboardDidShow",
      () => {
        // Small delay to allow SidekickView's padding adjustment to finish
        setTimeout(() => {
          scrollViewRef.current?.scrollToEnd({ animated: true })
        }, 150)
      },
    )

    return () => {
      showSubscription.remove()
    }
  }, [])

  const params = useLocalSearchParams<{
    messageId?: string
    messageText?: string
    rating?: string
  }>()

  // Parse the rating from params, defaulting to THUMBS_UP
  const initialRating =
    params.rating === FeedbackRating.THUMBS_DOWN
      ? FeedbackRating.THUMBS_DOWN
      : FeedbackRating.THUMBS_UP

  // Reconstruct a minimal message object for the form
  const selectedMessage = useMemo(() => {
    if (!params.messageId && !params.messageText) return undefined
    return {
      message_id: params.messageId,
      text: params.messageText,
    }
  }, [params.messageId, params.messageText])

  const handleClose = useCallback(() => {
    Keyboard.dismiss()
    // Small delay to allow keyboard to fully dismiss before navigating back
    // This prevents keyboard flickering when the chat screen's ChatTextInput auto-focuses
    setTimeout(() => {
      backOrToRoot()
    }, 100)
  }, [])

  const submitFeedback = async (feedback: string, selectedTagIds: string[]) => {
    const message = selectedMessage?.text ?? "null"
    const messageId = selectedMessage?.message_id ?? ""
    const response = await api.submitFeedback(
      message,
      messageId,
      feedback,
      initialRating,
      getAppVersion() ?? "Unknown",
      selectedTagIds.length > 0 ? selectedTagIds : undefined,
    )

    if (response.kind !== "ok") {
      Alert.alert("Error submitting feedback")
      throw new Error("Failed to submit feedback")
    } else {
      Keyboard.dismiss()
      // Small delay to allow keyboard to fully dismiss before navigating back
      // This prevents keyboard flickering when the chat screen's ChatTextInput auto-focuses
      setTimeout(() => {
        backOrToRoot()
      }, 100)
    }
  }

  return (
    <View style={themed($container)}>
      <StatusBar style={navTheme.dark ? "light" : "dark"} />

      <SidekickView
        showAvatar
        autoShowAvatarOnSpeech
        showCaptions
        showCCToggle
        showButtonCluster
        compactButtonCluster
        darkBackground={true}
        showControlsBarBackground={true}
        style={themed($container)}
        onMenuPress={handleClose}
        riveOwner="video"
        controlsConfig={{ type: "none" }}
      >
        {/* Custom Header - positioned above the content */}
        <View
          style={[
            themed($headerContainer),
            {
              top: insets.top,
              left: insets.left,
              right: insets.right,
            },
          ]}
          pointerEvents="box-none"
        >
          <Text style={themed($headerTitle)}>Provide Feedback</Text>
          <CloseButton
            onPress={handleClose}
            accessibilityLabel="Close feedback"
            style={{ position: "absolute", top: 22, right: 16 }}
          />
        </View>
        {/* Feedback Content */}
        <ScrollView
          ref={scrollViewRef}
          style={$scrollView}
          contentContainerStyle={[
            $scrollContent,
            {
              paddingTop: 60, // Space for header (SidekickView provides insets.top)
              paddingBottom: 40, // Breathing room at the bottom
            },
          ]}
          keyboardShouldPersistTaps="handled"
        >
          <FeedbackForm
            selectedMessage={selectedMessage}
            onSubmit={submitFeedback}
            initialRating={initialRating}
          />
        </ScrollView>
      </SidekickView>
    </View>
  )
}

const $container: ThemedStyle<ViewStyle> = ({ colors }) => ({
  flex: 1,
  backgroundColor: colors.background,
})

const $headerContainer: ThemedStyle<ViewStyle> = () => ({
  position: Platform.OS === "web" ? ("fixed" as any) : "absolute",
  zIndex: 2100,
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  paddingHorizontal: 16,
  height: 80,
  // No backgroundColor — see menu.tsx for rationale (avoid occluding the
  // tab-mode avatar that lives in MediaPlayerShell's foreground layer).
})

const $headerTitle: ThemedStyle<TextStyle> = ({ colors }) => ({
  fontSize: 18,
  fontWeight: "700",
  color: colors.text,
})

const $scrollView: ViewStyle = { flex: 1 }

const $scrollContent: ViewStyle = { paddingHorizontal: 16 }
